import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mic, Shield, Sparkles } from "lucide-react";
import i18n from "../../lib/i18n";

import { httpsCallable } from "firebase/functions";
import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  type UserCredential,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { functions, firebaseAuth, db } from "../../firebase";
import { useUser } from "../../context/UserContext";
import { userApi } from "../../api/user.api";
import logoImg from "../../assets/logo.png";
import "./LoginScreen.css";

import EmailStep from "./components/steps/EmailStep";
import OtpVerifyStep from "./components/steps/OtpVerifyStep";
import PasswordStep from "./components/steps/PasswordStep";
import RegisterStep from "./components/steps/RegisterStep";
import GoogleLinkOtpStep from "./components/steps/GoogleLinkOtpStep";
import GoogleLinkPasswordStep from "./components/steps/GoogleLinkPasswordStep";
import ForgotOtpStep from "./components/steps/ForgotOtpStep";
import ForgotNewPasswordStep from "./components/steps/ForgotNewPasswordStep";
import TotpVerifyStep from "./components/TotpVerifyStep";
import ReactivateAccountModal, { type ReactivateResult } from "./components/ReactivateAccountModal";

// email → otp-verify (new user only) → register → /main (profile setup modal on main)
// email → password (existing user) → /main
// email → google-link-otp (google-only user) → google-link-password → /main (password linked)
// password → forgot-otp → forgot-new-password → /main
type Step =
  | "email"
  | "otp-verify"
  | "password"
  | "register"
  | "google-link-otp"
  | "google-link-password"
  | "totp-verify"
  | "forgot-otp"
  | "forgot-new-password";

const isGoogleHostedProfileUrl = (url: string): boolean => {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return (
      hostname.endsWith("googleusercontent.com") ||
      hostname.endsWith("ggpht.com")
    );
  } catch {
    return false;
  }
};

export default function LoginScreen() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { refreshUserProfile } = useUser();

  const [step, setStep] = useState<Step>("email");
  const [otpMode, setOtpMode] = useState<"signup" | "refresh">("signup");
  const [currentEmail, setCurrentEmail] = useState("");
  const [googleLinkOtpCode, setGoogleLinkOtpCode] = useState("");
  const [forgotOtpCode, setForgotOtpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [pendingUid, setPendingUid] = useState("");
  const [reactivateOpen, setReactivateOpen] = useState(false);

  const handleReactivateSuccess = async (result: ReactivateResult) => {
    setReactivateOpen(false);
    if (result.twoFactorEnabled) {
      // Account is restored but sign-in requires the second factor — route to
      // the existing TOTP step, which calls verifyTwoFactorLogin and signs in
      // via custom token on success. Recovery factor (email OTP) does not
      // bypass 2FA the user opted into.
      setPendingUid(result.uid);
      setServerError("");
      setStep("totp-verify");
      return;
    }
    if (!result.customToken) {
      // Server should always send either customToken or twoFactorEnabled — defensive only.
      setServerError(t("errors.reactivationCompleteSignInFailed"));
      return;
    }
    await signInWithCustomToken(firebaseAuth, result.customToken);
    await refreshUserProfile();
    navigate("/main", { replace: true });
  };

  const openReactivateForEmail = (email: string) => {
    setCurrentEmail(email);
    setServerError("");
    setReactivateOpen(true);
  };

  // ─── Step 1: Email Continue ───────────────────────────────────────────────
  const handleEmailContinue = async (email: string) => {
    setCurrentEmail(email);
    setServerError("");
    try {
      setLoading(true);
      const checkEmail = httpsCallable(functions, "checkEmailExists");
      const result = await checkEmail({ email });
      const data = result.data as { exists: boolean; isGoogleOnly: boolean; passwordSet: boolean };

      if (data.exists) {
        if (data.isGoogleOnly) {
          const sendCode = httpsCallable(functions, "sendLoginCode");
          await sendCode({ email });
          setStep("google-link-otp");
        } else if (!data.passwordSet) {
          // Account exists but password was never set.
          // sendLoginCode sends to existing users; verifyLoginCode returns a customToken
          // so signInWithCustomToken gives a fresh auth_time for setInitialPassword.
          const sendCode = httpsCallable(functions, "sendLoginCode");
          await sendCode({ email });
          setOtpMode("refresh");
          setStep("otp-verify");
        } else {
          setStep("password");
        }
      } else {
        const sendCode = httpsCallable(functions, "sendSignupCode");
        await sendCode({ email });
        setStep("otp-verify");
      }
    } catch (err: any) {
      setServerError(err.message || t("errors.genericRetry"));
    } finally {
      setLoading(false);
    }
  };

  // ─── Step 2a: OTP Verify ─────────────────────────────────────────────────
  // otpMode "signup"  → verifySignupCode (new account)
  // otpMode "refresh" → verifyLoginCode  (passwordSet: false, fresh session needed)
  const handleOtpVerify = async (code: string) => {
    setServerError("");
    try {
      setLoading(true);
      if (otpMode === "refresh") {
        const verifyCode = httpsCallable<
          { email: string; code: string },
          { success: boolean; customToken: string; uid: string }
        >(functions, "verifyLoginCode");
        const result = await verifyCode({ email: currentEmail, code });
        await signInWithCustomToken(firebaseAuth, result.data.customToken);
        setOtpMode("signup");
        setStep("register");
      } else {
        const verifyCode = httpsCallable<
          { email: string; code: string },
          { success: boolean; customToken: string; uid: string }
        >(functions, "verifySignupCode");
        const result = await verifyCode({ email: currentEmail, code });
        await signInWithCustomToken(firebaseAuth, result.data.customToken);
        setStep("register");
      }
    } catch (err: any) {
      if (err.code === "functions/permission-denied") {
        setServerError(t("errors.invalidCode"));
      } else if (err.code === "functions/deadline-exceeded") {
        setServerError(t("errors.codeExpired"));
      } else if (err.code === "functions/resource-exhausted") {
        setServerError(t("errors.tooManyAttempts"));
      } else {
        setServerError(err.message || t("errors.verificationFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setServerError("");
    try {
      setLoading(true);
      const fnName = otpMode === "refresh" ? "sendLoginCode" : "sendSignupCode";
      const sendCode = httpsCallable(functions, fnName);
      await sendCode({ email: currentEmail });
    } catch {
      setServerError(t("errors.resendFailed"));
    } finally {
      setLoading(false);
    }
  };

  // ─── Step 2b: Sign In (existing user) ────────────────────────────────────
  const handleSignIn = async (password: string) => {
    setServerError("");
    try {
      setLoading(true);
      const credential = await signInWithEmailAndPassword(
        firebaseAuth,
        currentEmail,
        password,
      );
      const uid = credential.user.uid;
      const userDoc = await getDoc(doc(db, "users", uid));
      const twoFactorEnabled =
        userDoc.exists() && userDoc.data()?.twoFactorEnabled === true;

      if (twoFactorEnabled) {
        await signOut(firebaseAuth);
        setPendingUid(uid);
        setStep("totp-verify");
      } else {
        navigate("/main", { replace: true });
      }
    } catch (err: any) {
      if (firebaseAuth.currentUser) {
        await signOut(firebaseAuth).catch(() => {});
      }
      if (err?.code === "auth/user-disabled") {
        // Password was correct (Firebase Auth checks password before disabled);
        // route to reactivation instead of showing a dead-end error.
        openReactivateForEmail(currentEmail);
      } else {
        setServerError(getAuthError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Step 3: Set Password (new user, after OTP + custom token sign-in) ───
  const handleRegister = async (password: string) => {
    if (!firebaseAuth.currentUser) {
      setServerError(t("errors.sessionExpiredStart"));
      setStep("email");
      return;
    }
    setServerError("");
    try {
      setLoading(true);
      const setPasswordFn = httpsCallable(functions, "setInitialPassword");
      await setPasswordFn({ password });
      await refreshUserProfile();
      navigate("/main", { replace: true });
    } catch (err: any) {
      if (err.code === "functions/failed-precondition") {
        // auth_time > 10 min — session too old for setInitialPassword.
        // Sign out and send a fresh login OTP so the user gets a new custom token.
        await signOut(firebaseAuth).catch(() => {});
        try {
          const sendCode = httpsCallable(functions, "sendLoginCode");
          await sendCode({ email: currentEmail });
          setOtpMode("refresh");
          setStep("otp-verify");
        } catch {
          setStep("email");
        }
        setServerError(t("errors.sessionExpiredVerify"));
      } else {
        setServerError(err.message || t("errors.setPasswordFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Google Link: OTP → password step ────────────────────────────────────
  const handleGoogleLinkOtpNext = (code: string) => {
    setGoogleLinkOtpCode(code);
    setServerError("");
    setStep("google-link-password");
  };

  const handleGoogleLinkResendOtp = async () => {
    setServerError("");
    try {
      setLoading(true);
      const sendCode = httpsCallable(functions, "sendLoginCode");
      await sendCode({ email: currentEmail });
    } catch {
      setServerError(t("errors.resendFailed"));
    } finally {
      setLoading(false);
    }
  };

  // ─── Google Link: Set password ────────────────────────────────────────────
  const handleGoogleLinkSetPassword = async (password: string) => {
    setServerError("");
    try {
      setLoading(true);
      const linkFn = httpsCallable<
        { email: string; code: string; newPassword: string },
        { success: boolean; customToken?: string; uid: string; twoFactorEnabled: boolean }
      >(functions, "linkEmailPasswordWithOtp");
      const result = await linkFn({
        email: currentEmail,
        code: googleLinkOtpCode,
        newPassword: password,
      });
      if (result.data.twoFactorEnabled) {
        setPendingUid(result.data.uid);
        setStep("totp-verify");
      } else {
        await signInWithCustomToken(firebaseAuth, result.data.customToken!);
        navigate("/main", { replace: true });
      }
    } catch (err: any) {
      if (err.code === "functions/permission-denied") {
        setServerError(t("errors.invalidCodeGoBack"));
      } else if (err.code === "functions/deadline-exceeded") {
        setServerError(t("errors.codeExpiredGoBack"));
      } else {
        setServerError(err.message || t("errors.setPasswordFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Forgot Password ──────────────────────────────────────────────────────
  const handleForgotPassword = async () => {
    setServerError("");
    try {
      setLoading(true);
      const sendCode = httpsCallable(functions, "sendPasswordResetCode");
      await sendCode({ email: currentEmail });
      setStep("forgot-otp");
    } catch (err: any) {
      if (err.code === "functions/resource-exhausted") {
        setServerError(err.message || t("errors.tooManyAttemptsLater"));
      } else {
        setServerError(t("errors.resetCodeSendFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotResendCode = async () => {
    setServerError("");
    try {
      setLoading(true);
      const sendCode = httpsCallable(functions, "sendPasswordResetCode");
      await sendCode({ email: currentEmail });
    } catch {
      setServerError(t("errors.resendFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotOtpNext = (code: string) => {
    setForgotOtpCode(code);
    setServerError("");
    setStep("forgot-new-password");
  };

  const handleForgotNewPassword = async (password: string) => {
    setServerError("");
    try {
      setLoading(true);
      const changePwFn = httpsCallable<
        { email: string; code: string; newPassword: string },
        { success: boolean; customToken?: string; uid: string; twoFactorEnabled: boolean }
      >(functions, "changePassword");
      const result = await changePwFn({
        email: currentEmail,
        code: forgotOtpCode,
        newPassword: password,
      });
      if (result.data.twoFactorEnabled) {
        setPendingUid(result.data.uid);
        setStep("totp-verify");
      } else {
        await signInWithCustomToken(firebaseAuth, result.data.customToken!);
        navigate("/main", { replace: true });
      }
    } catch (err: any) {
      if (err.code === "functions/permission-denied") {
        setServerError(t("errors.invalidCodeGoBack"));
      } else if (err.code === "functions/deadline-exceeded") {
        setServerError(t("errors.resetCodeExpiredGoBack"));
      } else if (err.code === "functions/not-found") {
        setServerError(t("errors.resetCodeNotFound"));
      } else if (err.code === "functions/resource-exhausted") {
        setServerError(err.message || t("errors.tooManyAttempts"));
      } else {
        setServerError(err.message || t("errors.setPasswordFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Google Sign-In ───────────────────────────────────────────────────────

  const processGoogleResult = useCallback(async (result: UserCredential) => {
    const firebaseUser = result.user;

    const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
    const existingProfileUrl =
      (userDoc.exists()
        ? (userDoc.data()?.profilePictureUrl as string | undefined)
        : "") || "";
    const profileComplete =
      userDoc.exists() && userDoc.data()?.profileComplete === true;
    const twoFactorEnabled =
      userDoc.exists() && userDoc.data()?.twoFactorEnabled === true;

    if (
      !profileComplete &&
      firebaseUser.photoURL &&
      (!existingProfileUrl || isGoogleHostedProfileUrl(existingProfileUrl))
    ) {
      try {
        await userApi.importGoogleProfilePicture(firebaseUser.photoURL);
      } catch (importError) {
        console.warn("Server-side Google profile import failed:", importError);
      }
    }

    if (twoFactorEnabled) {
      await signOut(firebaseAuth);
      setPendingUid(firebaseUser.uid);
      setStep("totp-verify");
    } else {
      navigate("/main", { replace: true });
    }
  }, [navigate, setPendingUid, setStep]);

  const handleGoogleSignIn = async () => {
    setServerError("");
    try {
      setLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(firebaseAuth, provider);
      await processGoogleResult(result);
    } catch (err: any) {
      if (firebaseAuth.currentUser) {
        await signOut(firebaseAuth).catch(() => {});
      }
      if (err?.code === "auth/user-disabled") {
        // Google account verified the user's email; firebase auth then rejected
        // because the account is disabled. Route to reactivation using the
        // email Google returned (fallback to whatever the user typed earlier).
        const errEmail = (err?.customData?.email as string | undefined) || currentEmail;
        if (errEmail) {
          openReactivateForEmail(errEmail);
        } else {
          setServerError(t("errors.googleAccountDisabled"));
        }
      } else if (err.code !== "auth/popup-closed-by-user" && err.code !== "auth/cancelled-popup-request") {
        setServerError(t("errors.googleSignInFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── TOTP 2FA Verify ──────────────────────────────────────────────────────
  const handleTotpVerify = async (code: string) => {
    if (!pendingUid) {
      setServerError(t("errors.sessionExpiredStart"));
      setStep("email");
      return;
    }
    setServerError("");
    try {
      setLoading(true);
      const verifyFn = httpsCallable<
        { uid: string; code: string },
        { success: boolean; customToken: string }
      >(functions, "verifyTwoFactorLogin");
      const result = await verifyFn({ uid: pendingUid, code });
      await signInWithCustomToken(firebaseAuth, result.data.customToken);
      navigate("/main", { replace: true });
    } catch (err: any) {
      if (err.code === "functions/permission-denied") {
        setServerError(t("errors.invalidAuthCode"));
      } else if (err.code === "functions/failed-precondition") {
        navigate("/main", { replace: true });
      } else {
        setServerError(err.message || t("errors.verificationFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ls-login-screen auth-screen">
      {/* Full-page decorative circles (mobile only) */}
      <div className="ls-bg-decor-circle-1" />
      <div className="ls-bg-decor-circle-2" />
      <div className="ls-bg-decor-circle-3" />

      {/* Left Panel */}
      <div className="ls-branding-panel">
        <div className="ls-brand-decor-circle-1" />
        <div className="ls-brand-decor-circle-2" />
        <div className="ls-brand-decor-circle-3" />
        <div className="ls-branding-content">
          <img src={logoImg} alt="Helixa AI" className="ls-brand-title-logo" />
          <p className="ls-brand-subtitle">{t("branding.subtitle")}</p>
          <div className="ls-feature-list">
            <div className="ls-feature-item">
              <div className="ls-feature-icon-container">
                <Mic size={15} color="rgba(255,255,255,0.9)" />
              </div>
              <span>{t("branding.feature1")}</span>
            </div>
            <div className="ls-feature-item">
              <div className="ls-feature-icon-container">
                <Shield size={15} color="rgba(255,255,255,0.9)" />
              </div>
              <span>{t("branding.feature2")}</span>
            </div>
            <div className="ls-feature-item">
              <div className="ls-feature-icon-container">
                <Sparkles size={15} color="rgba(255,255,255,0.9)" />
              </div>
              <span>{t("branding.feature3")}</span>
            </div>
          </div>
        </div>
        <div className="ls-branding-footer">
          <div className="ls-footer-badge">
            <div className="ls-footer-badge-dot" />
            <p className="ls-footer-text">{t("branding.trustBadge")}</p>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="ls-form-panel">
        <div className="ls-form-wrapper">
          <div className="ls-form-card">
            {step === "email" && (
              <EmailStep
                loading={loading}
                serverError={serverError}
                onSubmit={handleEmailContinue}
                onGoogleSignIn={handleGoogleSignIn}
              />
            )}
            {step === "otp-verify" && (
              <OtpVerifyStep
                email={currentEmail}
                loading={loading}
                serverError={serverError}
                onSubmit={handleOtpVerify}
                onBack={() => { setStep("email"); setServerError(""); }}
                onResend={handleResendOtp}
              />
            )}
            {step === "password" && (
              <PasswordStep
                email={currentEmail}
                loading={loading}
                serverError={serverError}
                onSubmit={handleSignIn}
                onBack={() => { setStep("email"); setServerError(""); }}
                onForgotPassword={handleForgotPassword}
              />
            )}
            {step === "register" && (
              <RegisterStep
                email={currentEmail}
                loading={loading}
                serverError={serverError}
                onSubmit={handleRegister}
                onBack={() => { setStep("otp-verify"); setServerError(""); }}
              />
            )}
            {step === "google-link-otp" && (
              <GoogleLinkOtpStep
                email={currentEmail}
                loading={loading}
                serverError={serverError}
                onSubmit={handleGoogleLinkOtpNext}
                onBack={() => { setStep("email"); setServerError(""); }}
                onResend={handleGoogleLinkResendOtp}
              />
            )}
            {step === "google-link-password" && (
              <GoogleLinkPasswordStep
                email={currentEmail}
                loading={loading}
                serverError={serverError}
                onSubmit={handleGoogleLinkSetPassword}
                onBack={() => { setStep("google-link-otp"); setServerError(""); }}
              />
            )}
            {step === "forgot-otp" && (
              <ForgotOtpStep
                email={currentEmail}
                loading={loading}
                serverError={serverError}
                onSubmit={handleForgotOtpNext}
                onBack={() => { setStep("password"); setServerError(""); }}
                onResend={handleForgotResendCode}
              />
            )}
            {step === "forgot-new-password" && (
              <ForgotNewPasswordStep
                loading={loading}
                serverError={serverError}
                onSubmit={handleForgotNewPassword}
                onBack={() => { setStep("forgot-otp"); setServerError(""); }}
              />
            )}
            {step === "totp-verify" && (
              <TotpVerifyStep
                loading={loading}
                serverError={serverError}
                onSubmit={handleTotpVerify}
                onBack={() => {
                  setStep("email");
                  setPendingUid("");
                  setServerError("");
                }}
              />
            )}
          </div>
        </div>
      </div>

      <ReactivateAccountModal
        isOpen={reactivateOpen}
        email={currentEmail}
        onSuccess={handleReactivateSuccess}
        onCancel={() => setReactivateOpen(false)}
      />
    </div>
  );
}

function getAuthError(err: any): string {
  switch (err.code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
      return i18n.t("auth:errors.incorrectPassword");
    case "auth/user-not-found":
      return i18n.t("auth:errors.accountNotFound");
    case "auth/email-already-in-use":
      return i18n.t("auth:errors.accountExists");
    case "auth/weak-password":
      return i18n.t("auth:errors.weakPassword");
    case "auth/too-many-requests":
      return i18n.t("auth:errors.tooManyAttemptsLater");
    case "auth/network-request-failed":
      return i18n.t("auth:errors.networkError");
    case "auth/requires-recent-login":
      return i18n.t("auth:errors.requiresRecentLogin");
    default:
      return err.message || i18n.t("auth:errors.genericRetry");
  }
}
